import { lazy } from "react";
import type { ComponentType } from "react";
import { notifyChunkLoadSuccess } from "./chunkReloadGuard";

type LazyImport<P> = () => Promise<{ default: ComponentType<P> }>;

const CHUNK_ERROR_SNIPPETS = [
  // Network-fetch failures: the chunk URL could not be retrieved.
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "Loading chunk",
  "Loading CSS chunk",
  // Export-mismatch failures: a stale chunk imports a binding the freshly
  // deployed sibling chunk no longer exports (post-deploy bundle skew).
  "Importing binding name", // Safari/WebKit: "Importing binding name 'A' is not found."
  "does not provide an export named", // Chrome/Firefox export mismatch
  // Stale index.html served where a JS module was expected.
  "Unexpected token '<'",
];

export class ChunkLoadError extends Error {
  public constructor(cause: unknown) {
    super("App update detected. Refresh to load the new version. You stay signed in.");
    this.name = "ChunkLoadError";
    if (cause instanceof Error) {
      this.cause = cause;
    }
  }
}

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const message = "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
  return CHUNK_ERROR_SNIPPETS.some((snippet) => message.includes(snippet));
};

export const lazyWithRetry = <P = object>(componentImport: LazyImport<P>) =>
  lazy(() =>
    componentImport()
      .then((module) => {
        // A dynamic chunk resolved → the current bundle graph is healthy.
        // Release any post-reload stale-chunk guard (event-based clear, ENT-1981)
        // so a *later* deploy can auto-recover again. This can only fire once an
        // import has actually resolved, never while one is still pending, so it
        // cannot race a slow/late-rejecting import the way a timer could.
        notifyChunkLoadSuccess();
        return module;
      })
      .catch((error) => {
        if (isChunkLoadError(error)) {
          throw new ChunkLoadError(error);
        }
        throw error;
      }),
  );
